import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import {
  Carousel,
  Spinner,
  Button,
} from 'react-bootstrap';
import BasePage from '../../../BasePage/BasePage';
import LiveInput from '../../../../atoms/LiveInput/LiveInput';
import Icon from '../../../../atoms/Icon/Icon';
import Sidebar from '../../../../molecules/Sidebar/Sidebar';
import Api from '../../../../../services/Api';
import { publishProduct } from '../../../../../helpers/publish';
import { fetchProductsInitAction, updateSelectedProductAction } from '../../../../../actions/instances/products';
import InstanceMetadata from '../../../../Organisms/InstanceMetadata/InstanceMetadata';
import DeleteModal from '../../../../molecules/DeleteModal/DeleteModal';
import DocumentsList from '../../../../Organisms/DocumentsList/DocumentsList';
import CitationList from '../../../../Organisms/CitationList/CitationList';
import { loadProductsMetadata } from '../../../../../helpers/metadata';
import CharacteristicsCardList from '../../../../Organisms/CharacteristicsCardList/CharacteristicsCardList';
import BuildingsList from '../../../../Organisms/BuildingsList/BuildingsList';
import ContextsList from '../../../../Organisms/ContextsList/ContextsList';
import './EditViewProduct.scss';

const EditViewProduct = () => {
  const {
    selectedProduct,
  } = useSelector(state => state.products);
  const { user } = useSelector(state => state.auth);
  const history = useHistory();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [characteristics, setCharacteristics] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [contexts, setContexts] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteModalShown, setIsDeleteModalShown] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  if (!selectedProduct) {
    history.push('/instances/building-products');
  }
  const loadMetadata = async () => {
    const metadata = await loadProductsMetadata(selectedProduct.id);
    setBuildings(metadata.buildings);
    setCharacteristics(metadata.characteristics);
    setContexts(metadata.contexts);
  };
  useEffect(() => {
    setIsLoading(true);
    loadMetadata().then(() => setIsLoading(false));
  }, []);
  const deleteProduct = () => {
    setIsDeleting(true);
    Api.delete(`/instances/building-products/${selectedProduct.id}`).then((response) => {
      if (response.status === 204) {
        dispatch(fetchProductsInitAction());
        setTimeout(() => {
          history.goBack();
          setIsDeleting(false);
        }, 500);
      } else {
        setIsDeleting(false);
      }
    });
  };
  const syncCharacteristics = async (newCharacteristic) => {
    setIsLoading(true);
    const productCharacteristic = await Api.get(`instances/building-components/${selectedProduct.id}`);
    const newIds = [
      ...productCharacteristic.data.characteristics.map(x => x.id),
      newCharacteristic.id,
    ];
    await Api.patch(`instances/building-components/${selectedProduct.id}`, {
      characteristics_ids: newIds,
    });
    const response = await loadProductsMetadata(selectedProduct.id);
    setCharacteristics(response.characteristics);
    setIsLoading(false);
  };
  const syncContexts = async (newContext) => {
    setIsLoading(true);
    const ids = contexts.map(x => x.id);
    await Api.patch(`instances/building-contexts/${selectedProduct.id}`, {
      contexts_ids: [...ids, newContext.id],
    });
    await loadMetadata();
    setIsLoading(false);
  };
  return (
    <BasePage>
      <DeleteModal
        onDelete={deleteProduct}
        isShown={isDeleteModalShown}
        isLoading={isDeleting}
        onClose={() => setIsDeleteModalShown(false)}
        isAllowed
      >
        Are you sure you want to delete
        {' '}
        <strong>{selectedProduct.name}</strong>
        {'? '}
        <br />
        This action cannot be undone.
      </DeleteModal>
      <div className="col-md-3">
        <div className="row">
          <div className="col-md-12">
            {selectedProduct.images && selectedProduct.images.length > 0 ? (
              <Carousel>
                {selectedProduct.images.map(image => (
                  <Carousel.Item>
                    <img
                      className="d-block w-100 img-fluid"
                      src={image.url}
                      alt={image.label}
                      style={{
                        maxHeight: '400px',
                        objectFit: 'cover',
                      }}
                    />
                  </Carousel.Item>
                ))}
              </Carousel>
            ) : (
              <img src="/images/default.jpg" alt={selectedProduct.name} className="img-fluid" />
            )}
          </div>
          <div className="col-md-12 mt-2 d-grid gap-2">
            {user.role_id !== 3 && (
              <Button
                variant={selectedProduct.in_review === 1 ? 'success' : 'warning'}
                onClick={async () => {
                  setIsPublishing(true);
                  const component = await publishProduct(
                    selectedProduct.id,
                    selectedProduct.in_review,
                  );
                  dispatch(updateSelectedProductAction(component));
                  dispatch(fetchProductsInitAction());
                  setIsPublishing(false);
                }}
              >
                {isPublishing ? (
                  <>
                    <Spinner
                      as="span"
                      animation="grow"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                    <span className="ms-2">Loading...</span>
                  </>
                ) : (
                  <>
                    <Icon icon="check" />
                    <span className={`ms-2 ${selectedProduct.in_review === 0 ? 'text-white' : ''}`}>
                      {selectedProduct.in_review === 1 ? 'Approve' : 'Disapprove'}
                    </span>
                  </>
                )}
              </Button>
            )}
            <Button
              variant="dark"
              onClick={() => {
                dispatch(updateSelectedProductAction(selectedProduct));
                history.push('/instances/building-products/edit');
              }}
            >
              <Icon icon="edit" />
              <span className="ms-2">Edit</span>
            </Button>
            <Button variant="dark" onClick={() => history.goBack()}>
              <Icon icon="times" />
              <span className="ms-2">Cancel</span>
            </Button>
            {user.role_id !== 3 && (
              <Button variant="danger" onClick={() => setIsDeleteModalShown(true)}>
                {isDeleting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="grow"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                    <span className="ms-2">Loading...</span>
                  </>
                ) : (
                  <>
                    <Icon icon="trash" />
                    <span className="ms-2">Delete</span>
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
      <div className="col-md-6">
        <div className="row d-flex align-items-center mb-3">
          <div className="col-md-1 text-end">
            <Icon icon="edit" color="#000" />
          </div>
          <div className="col-md-11 text-start">
            <LiveInput endpoint="/instances/building-products" fieldName="name" id={selectedProduct.id} value={selectedProduct.name} fontSize={22} />
          </div>
        </div>
        <div className="row border-top border-bottom mt-2 p-2">
          <div className="col-md-12">
            <div className="border-end d-inline pe-2 me-2">
              <span className="me-1">
                <Icon icon="calendar" color="#000" />
              </span>
              {moment(selectedProduct.cerated_at).format('DD-MM-YYYY')}
            </div>
            <div className="d-inline">
              {selectedProduct.tags.map(tag => (
                <span className="me-2 border-end pe-2">
                  <Icon icon="tag" color="#000" />
                  <span className="ms-2">
                    {tag.name}
                  </span>
                </span>
              ))}
            </div>
            <div className="d-inline border-end d-inline pe-2 me-2">
              <Icon icon="map" color="#000" />
              <span className="ms-2">
                {`${selectedProduct.city.name}, ${selectedProduct.city.country.name} [${selectedProduct.city.code}]`}
              </span>
            </div>
            <div className="d-inline">
              <Icon icon="globe" color="#000" />
              <span className="ms-2">
                {`${selectedProduct.dynasty.name} [${selectedProduct.dynasty.code}]`}
              </span>
            </div>
          </div>
        </div>
        <InstanceMetadata instance={selectedProduct} title="Product metadata" />
        <BuildingsList buildings={buildings} />
        <ContextsList
          contexts={contexts}
          isLoading={isLoading}
          onItemAdded={con => syncContexts(con)}
          cityId={selectedProduct.city.id}
          dynastyId={selectedProduct.dynasty.id}
        />
        <DocumentsList documents={selectedProduct ? selectedProduct.documents : []} title="Documents" />
        <CitationList citations={selectedProduct ? selectedProduct.citation : []} title="Citations" />
      </div>
      <div className="col-md-3 mt-5">
        <CharacteristicsCardList
          characteristics={characteristics}
          cityId={selectedProduct.city.id}
          dynastyId={selectedProduct.dynasty.id}
          isLoading={isLoading}
          onItemAdded={char => syncCharacteristics(char)}
        />
        <Sidebar />
      </div>
    </BasePage>
  );
};

export default EditViewProduct;
